import {
  CrmDealListQuerySchema,
  type CrmDealHelpReason,
  type CrmDealList,
  type CrmDealListQuery,
  type CrmDealReplyState,
} from "@vekha/contracts";

import { fetchCrmDeals } from "./api";

/** The dialog list's reads: the queue's page and the queue counters, as «Диалоги» ask them for an address and a work
 * context. One definition for the page and for the start of the app (main.tsx): 18.10.2026 the list was asked only after
 * the session check and the snapshot's part — two round trips — because its agent and scenario come from the snapshot.
 * The app now asks it at once with the context this browser resolved last time; the page takes that answer only when its
 * own request is exactly the same, otherwise it asks as before. */
export type DialogsView = "attention" | "needs_reply" | "waiting_customer" | "no_messages" | "help" | "all" | "yana_day";

const VIEWS: readonly DialogsView[] = ["attention", "needs_reply", "waiting_customer", "no_messages", "help", "all", "yana_day"];

export const DIALOGS_HELP_REASONS: readonly CrmDealHelpReason[] = [
  "human_control",
  "ambiguous_link",
  "no_active_conversation",
  "conversation_handoff",
];

/** The queue the address names; «Нужно ответить» without one. */
export function dialogsView(value: string | null): DialogsView {
  return VIEWS.includes(value as DialogsView) ? value as DialogsView : "needs_reply";
}

export function dialogsHelpReason(value: string | null): CrmDealHelpReason | "" {
  return DIALOGS_HELP_REASONS.includes(value as CrmDealHelpReason) ? value as CrmDealHelpReason : "";
}

export function quickFilters(view: DialogsView): {
  requiresAttention: boolean;
  replyStates: CrmDealReplyState[];
  helpReasons: CrmDealHelpReason[];
} {
  if (view === "attention") return { requiresAttention: true, replyStates: [], helpReasons: [] };
  if (view === "needs_reply") return { requiresAttention: false, replyStates: ["needs_reply"], helpReasons: [] };
  if (view === "waiting_customer") return { requiresAttention: false, replyStates: ["waiting_customer"], helpReasons: [] };
  if (view === "no_messages") return { requiresAttention: false, replyStates: ["no_messages"], helpReasons: [] };
  if (view === "help") return { requiresAttention: false, replyStates: [], helpReasons: [...DIALOGS_HELP_REASONS] };
  return { requiresAttention: false, replyStates: [], helpReasons: [] };
}

export type DialogsListAddress = Readonly<{
  search: string;
  employeeIds: readonly string[];
  scenarioIds: readonly string[];
  pipelineId: string;
  stageId: string;
  channel: string;
  control: string;
  helpReason: CrmDealHelpReason | "";
}>;

/** The queue counters' filters: every queue of the work context. */
export function dialogsListBase(address: DialogsListAddress): Partial<CrmDealListQuery> {
  return {
    search: address.search,
    employeeIds: [...address.employeeIds],
    scenarioIds: [...address.scenarioIds],
    // The operator queue includes configured CRM deals even before a personal
    // chat has been linked. Resolver execution remains gated separately by
    // exact responsibility, route and conversation identity.
    conversationPresence: "any",
    pipelineIds: address.pipelineId ? [address.pipelineId] : [],
    stageIds: address.stageId ? [address.stageId] : [],
    channels: address.channel ? [address.channel] : [],
    controlModes: address.control === "employee" || address.control === "human" ? [address.control] : [],
    helpReasons: address.helpReason ? [address.helpReason] : [],
    limit: 30,
  };
}

/** The open queue's rows. */
export function dialogsListPage(
  base: Partial<CrmDealListQuery>,
  view: DialogsView,
  helpReason: CrmDealHelpReason | "",
): Partial<CrmDealListQuery> {
  const quick = quickFilters(view);
  return {
    ...base,
    requiresAttention: quick.requiresAttention,
    replyStates: quick.replyStates,
    helpReasons: helpReason ? [helpReason] : quick.helpReasons,
  };
}

/** «Все» without a help filter is the queue request itself; its facets already are the queue counts. */
export function pageIsQueue(page: Partial<CrmDealListQuery>): boolean {
  return !page.requiresAttention && page.replyStates?.length === 0 && page.helpReasons?.length === 0;
}

export const queueCounters = (base: Partial<CrmDealListQuery>): Partial<CrmDealListQuery> => ({ ...base, limit: 1 });

type RememberedContext = Readonly<{ agent: string; scenario: string; employeeIds: string[]; scenarioIds: string[] }>;
const CONTEXT_KEY = "vekha.dialogs.context";
/** As lib/startup-boot.ts: a session idles out after 12 h without requests; an older context does not start reads. */
const FRESH_MS = 11 * 60 * 60 * 1_000;

/** The work context the page resolved (the address's agent and scenario and the ids the list is asked with). Only ids —
 * no client data. */
export function rememberDialogsContext(context: RememberedContext) {
  try { localStorage.setItem(CONTEXT_KEY, JSON.stringify({ ...context, at: Date.now() })); } catch { /* storage off: the list simply waits */ }
}

// The names of the page's first script (lib/startup-boot.ts); repeated here so the app's code shares nothing with it.
export const STARTUP_READS_KEY = "vekha.startup.reads";
export const STARTUP_READY = "vekha:startup-ready";
export const APP_STARTED = "vekhaAppStarted";

/** The first list is on screen: the page's first script remembers the reads of this start for the next one. */
export function signalStartupReady() {
  try { window.dispatchEvent(new Event(STARTUP_READY)); } catch { /* no page script */ }
}

/** Signed out, or the session is gone: the next start asks nothing before the session check (the login page is not
 * refused anything). */
export function forgetDialogsContext() {
  try {
    localStorage.removeItem(CONTEXT_KEY);
    localStorage.removeItem(STARTUP_READS_KEY);
  } catch { /* nothing remembered */ }
}

function rememberedContext(): RememberedContext | null {
  try {
    const value = JSON.parse(localStorage.getItem(CONTEXT_KEY) ?? "null") as (Partial<RememberedContext> & { at?: unknown }) | null;
    const ids = (list: unknown): list is string[] => Array.isArray(list) && list.every((id) => typeof id === "string");
    if (!value || typeof value.agent !== "string" || typeof value.scenario !== "string") return null;
    if (typeof value.at !== "number" || Date.now() - value.at >= FRESH_MS) return null;
    if (!ids(value.employeeIds) || !ids(value.scenarioIds) || value.scenarioIds.length === 0) return null;
    return value as RememberedContext;
  } catch {
    return null;
  }
}

const requestKey = (input: Partial<CrmDealListQuery>) => JSON.stringify(CrmDealListQuerySchema.parse(input));
const early = new Map<string, Promise<CrmDealList>>();
/** An answer nobody took (the page went elsewhere) is not kept: a later list must be fresh. */
const KEEP_MS = 30_000;

function startEarly(input: Partial<CrmDealListQuery>) {
  const key = requestKey(input);
  if (early.has(key)) return;
  const answer = fetchCrmDeals(input);
  answer.catch(() => undefined);
  early.set(key, answer);
  setTimeout(() => { if (early.get(key) === answer) early.delete(key); }, KEEP_MS);
}

/** At the start of the app on «Диалоги»: the list's reads for the address, with the context this browser resolved last
 * time, when the address does not name another agent or scenario. True when they were started — the browser has opened
 * «Диалоги» signed in before. */
export function startDialogsListReads(search: string): boolean {
  const context = rememberedContext();
  if (context === null) return false;
  const params = new URLSearchParams(search);
  const agent = params.get("agent");
  const scenario = params.get("scenario");
  if ((agent !== null && agent !== context.agent) || (scenario !== null && scenario !== context.scenario)) return false;
  const view = dialogsView(params.get("view"));
  const helpReason = dialogsHelpReason(params.get("help"));
  const base = dialogsListBase({
    search: params.get("q") ?? "",
    employeeIds: context.employeeIds,
    scenarioIds: context.scenarioIds,
    pipelineId: params.get("pipeline") ?? "",
    stageId: params.get("stage") ?? "",
    channel: params.get("channel") ?? "",
    control: params.get("control") ?? "",
    helpReason,
  });
  const page = dialogsListPage(base, view, helpReason);
  if (view !== "yana_day") startEarly(page);
  if (view === "yana_day" || !pageIsQueue(page)) startEarly(queueCounters(base));
  return true;
}

/** The page's first list takes the read started at load for exactly its request (a failed one is asked again); every
 * other read is fresh. Whatever the first list did not take is dropped then. */
export function readDealList(input: Partial<CrmDealListQuery>, first: boolean): Promise<CrmDealList> {
  if (!first) return fetchCrmDeals(input);
  const key = requestKey(input);
  const answer = early.get(key);
  early.delete(key);
  return answer ? answer.catch(() => fetchCrmDeals(input)) : fetchCrmDeals(input);
}

export function dropEarlyDealLists() { early.clear(); }
